/* @flow */
import styled, { css } from "styled-components";
import { Row } from "ignite-components";
import { darken } from "polished";

const colors = {
    border: "#c7c7c7",
    borderSelected: "#7bc2c7",
    headerBackground: "#eaeaea",
    hover: "#f3f3f3",
    fixed: "#f5f5f5",
};

let headerHeight: number = 80;
let cellHeight: number = 25;

let progressPadding: number = 0.8;
let contentSpace: number = 2;

interface TableContainerProps {
    height: string;
}

export const TableContainer = styled.div<TableContainerProps>`
    border-collapse: collapse;

    height: ${props => props.height};
`;

export const TableHeader = styled(Row)`
    height: ${headerHeight}px;
    align-items: center;
`;

interface TableBodyContainerProps {
    leftPadding?: number;
}

export const TableBodyContainer = styled.div<TableBodyContainerProps>`
    width: 100%;
    height: calc(100% - ${cellHeight + headerHeight}px);
    border-collapse: collapse;
    position: relative;
    padding-left: ${props => (props.leftPadding ? props.leftPadding : 0)}px;
    border-bottom: 1px solid ${colors.border};
    border-right: 1px solid ${colors.border};
    border-left: 1px solid ${colors.border};
`;

export const FixedColumnContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    overflow: scroll;
    border-collapse: collapse;
    padding-bottom: 10px;
    &::-webkit-scrollbar {
        display: none;
    }
`;

export const ColumnContainer = styled.div`
    width: 100%;
    height: 100%;
    overflow: scroll;
    border-collapse: collapse;

    /* width */
    &::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 1337px;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
`;

interface TableHeaderContainerProps {
    leftPadding?: number;
}

export const TableHeaderContainer = styled.div<TableHeaderContainerProps>`
    width: 100%;
    position: relative;
    padding-right: 10px;
    background: ${colors.headerBackground};
    padding-left: ${props => (props.leftPadding ? props.leftPadding : 0)}px;
    border-collapse: collapse;
    border-top: 1px solid ${colors.border};
    border-right: 1px solid ${colors.border};
    border-left: 1px solid ${colors.border};
`;

export const FixedHeaderContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    border-collapse: collapse;
`;

export const HeadContainer = styled.div`
    width: 100%;
    overflow: scroll;
    border-collapse: collapse;
    &::-webkit-scrollbar {
        display: none;
    }
`;

interface StyledTableProps {
    height?: number;
}

export const StyledTable = styled.table<StyledTableProps>`
    border-spacing: 0;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    height: ${props => (props.height ? props.height : "auto")};
    overflow: scroll;

    background: #fff;
    color: #333;
`;

export const StyledHeader = styled.thead`
    position: relative;
    z-index: 9999;
    will-change: top;
    border-collapse: collapse;
`;

export const StyledTableContent = styled.tbody`
    position: relative;
    border-collapse: collapse;
`;

export const Tr = styled.tr`
    border-collapse: collapse;
    border-bottom: 1px solid ${colors.border};
`;

interface TdProps {
    progress?: boolean;
    value?: number;
    fixed?: boolean;
    hover?: boolean;
    width?: number;
    height?: number;
    percent?: number;
    progressColor?: string;
}

export const Td = styled.td<TdProps>`
    padding: ${props => (props.progress ? 0 : "0 5px")};
    overflow: hidden;
    text-overflow: ellipsis;
    border-collapse: collapse;
    background: ${props => {
        if (props.fixed) {
            return props.hover ? darken(0.05, colors.fixed) : colors.fixed;
        }

        return "#fff";
    }};
    border-right: 1px solid ${colors.border};

    min-width: ${props => (props.width ? props.width : 200)}px;
    max-width: 400px;
    height: ${props => (props.height ? props.height : 25)}px;

    ${props =>
        props.progress != null
            ? css`
          position: relative;
          
          &:before {
            content: '';
            height: 10px;
            width: ${() => {
                if (props.percent && props.percent < 0) {
                    return 0;
                }

                if (props.percent && props.percent > 100) {
                    return 100 * progressPadding;
                }

                return props.percent ? props.percent * progressPadding : 0;
            }}%;
            display: block;
            background: ${props.progressColor ? props.progressColor : "#6b77ee"};
            color: ${props.percent && props.percent > 20 ? "#fff" : "inherit"};
          }
          
          &:after {
            content: '${props.value}';
            display: inline-block;
            position: absolute;
            top: 50%;
            margin: -7px auto;
            
            left: ${() => {
                if (props.percent && props.percent < 0) {
                    return contentSpace;
                }

                if (props.percent && props.percent > 100) {
                    return 100 * progressPadding + contentSpace;
                }

                return props.percent ? props.percent * progressPadding + contentSpace : contentSpace;
            }}%;
          }
        `
            : null}

    &:hover {
        background: ${props => darken(0.05, props.fixed ? colors.fixed : "#fff")};
    }
`;

export const Th = styled(Td.withComponent("th"))`
    background: ${props => (props.hover ? darken(0.05, colors.headerBackground) : colors.headerBackground)};

    &:hover {
        background: ${darken(0.05, colors.headerBackground)};
    }
`;
